import {
  afterNextRender,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';

import { Category } from '@services/categories/interfaces/categories.interfaces';

import { LANDING_TEXTS } from '@constants/text.constants';

@Component({
  selector: 'app-category-section',
  standalone: true,
  templateUrl: './category-section.component.html',
  styleUrls: ['./category-section.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class CategorySectionComponent implements OnChanges, OnDestroy {
  @Input() public categories: Category[] = [];
  @ViewChild('swiperEl') private swiperEl!: ElementRef<HTMLElement>;

  public readonly pageTexts = LANDING_TEXTS;

  private viewInitialized = false;
  private swiperInstance?: Swiper;
  private pendingSwiperInitTimeoutId?: number;

  constructor(private ngZone: NgZone) {
    afterNextRender(() => {
      this.viewInitialized = true;
      if (this.categories.length > 0) {
        this.initSwiper();
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.viewInitialized && changes['categories']?.currentValue?.length > 0) {
      this.destroySwiper();
      this.clearPendingSwiperInit();
      this.ngZone.runOutsideAngular(() => {
        this.pendingSwiperInitTimeoutId = window.setTimeout(() => {
          this.pendingSwiperInitTimeoutId = undefined;
          this.initSwiper();
        }, 0);
      });
    }
  }

  public ngOnDestroy(): void {
    this.clearPendingSwiperInit();
    this.destroySwiper();
  }

  private clearPendingSwiperInit(): void {
    if (this.pendingSwiperInitTimeoutId !== undefined) {
      window.clearTimeout(this.pendingSwiperInitTimeoutId);
      this.pendingSwiperInitTimeoutId = undefined;
    }
  }

  private destroySwiper(): void {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = undefined;
    }
  }

  private initSwiper(): void {
    if (this.categories.length === 0 || !this.swiperEl?.nativeElement) {
      return;
    }

    this.swiperInstance = new Swiper(this.swiperEl.nativeElement, {
      modules: [Navigation, Pagination, Autoplay],
      slidesPerView: 1,
      spaceBetween: 20,
      loop: this.categories.length > 3,
      grabCursor: true,
      autoplay: {
        delay: 4000,
        disableOnInteraction: true,
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        dynamicBullets: true,
      },
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 30,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 40,
        },
      },
    });
  }
}
